import { useEffect, useRef, useContext } from "react"
import { useDispatch } from "react-redux"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { LOADING } from "helpers/data-statuses"
import { useIntradayMode } from "./use-intraday-mode"
import { DashboardContext } from "../context/dashboard"
import { IDashboardContextValue } from "../context/types"
import { useIsDashboardOptimizeExperimentsFeatureEnabled } from "resources/account"

/**
 * Custom hook to handle intraday warning notifications
 *
 * Intraday mode is defined as:
 * 1. Intraday feature flag enabled
 * 2. Current main dashboard instrument is an intraday-enabled portfolio
 * 3. Intraday data has been uploaded for that portfolio
 * 4. End date is intraday date (today)
 *
 * Shows an intraday data warning toast when 1, 2 are true but 3 is false (4 is false inherently)
 * Shows an optimization warning toast when 1 - 4 are true and optimization is enabled
 */
export function useIntradayWarningNotification() {
  const dispatch = useDispatch()
  const intradayDataWarningShownRef = useRef(false)
  const optimizationWarningShownRef = useRef(false)
  const { isIntraday } = useContext<IDashboardContextValue>(DashboardContext)
  const [{ intradayDataAvailable, isIntradayMode }, intradayBatchStatus] = useIntradayMode()
  const isOptimizeExperimentsEnabled = useIsDashboardOptimizeExperimentsFeatureEnabled()

  useEffect(() => {
    // Only show warning if intraday is enabled AND no intraday data is available AND not currently loading
    if (isIntraday && !intradayDataAvailable && intradayBatchStatus !== LOADING) {
      if (!intradayDataWarningShownRef.current) {
        intradayDataWarningShownRef.current = true
        dispatch(NotificationCreators.add("warning", null, "No intraday data. Using end of previous day."))
      }
    } else if (!isIntraday || intradayDataAvailable) {
      // Reset the warning flag when:
      // - Intraday is not enabled, OR
      // - Intraday data becomes available
      intradayDataWarningShownRef.current = false
    }
  }, [isIntraday, intradayDataAvailable, intradayBatchStatus, dispatch])

  useEffect(() => {
    // Only show warning if intraday mode is enabled AND optimization is enabled
    if (isIntradayMode && isOptimizeExperimentsEnabled) {
      if (!optimizationWarningShownRef.current) {
        optimizationWarningShownRef.current = true
        dispatch(NotificationCreators.add("warning", null, "Optimization strategy is not available for intraday data."))
      }
    } else {
      optimizationWarningShownRef.current = false
    }
  }, [isIntradayMode, isOptimizeExperimentsEnabled, dispatch])
}
